import { useConfirm } from "../../components/common";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatTL, formatDateTime, STATUS_LABELS } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { AssignSuggest, PaymentBox, ChecklistView } from "../../components/JobExtras";
import DiscountBox from "../../components/DiscountBox";
import NoteAssist from "../../components/NoteAssist";
import ConflictHint from "../../components/ConflictHint";
import JobMessageDialog from "../../components/JobMessageDialog";
import ManualJobDialog from "../../components/ManualJobDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { ArrowLeft, Save, FileDown, Trash2, Plus, RefreshCw, AlertCircle, Pencil } from "lucide-react";
import { toast } from "sonner";
import { generateServicePDF } from "../../lib/pdfGenerator";

export default function JobDetail() {
  const [ask, confirmEl] = useConfirm();
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [techs, setTechs] = useState([]);
  const [parts, setParts] = useState([]);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [reassignForm, setReassignForm] = useState({ technician_id: "", note: "", keep_history: true });

  const load = async () => {
    const [j, t, p] = await Promise.all([
      api.get(`/jobs/${id}`), api.get("/technicians"), api.get("/parts")
    ]);
    setJob(j.data); setTechs(t.data); setParts(p.data);
  };

  useEffect(() => { load(); }, [id]);

  if (!job) return <div>Yükleniyor...</div>;

  const updateField = (k, v) => setJob({ ...job, [k]: v });

  const addPart = () => {
    const used = job.parts_used || [];
    setJob({ ...job, parts_used: [...used, { part_id: "", name: "", qty: 1, unit_price: 0, total: 0 }] });
  };
  const removePart = (i) => {
    const used = [...(job.parts_used || [])]; used.splice(i, 1);
    setJob({ ...job, parts_used: used });
  };
  const updatePart = (i, k, v) => {
    const used = [...(job.parts_used || [])];
    used[i] = { ...used[i], [k]: v };
    if (k === "part_id") {
      const p = parts.find(p => p.id === v);
      if (p) { used[i].name = p.name; used[i].unit_price = p.price; }
    }
    used[i].total = (Number(used[i].qty) || 0) * (Number(used[i].unit_price) || 0);
    setJob({ ...job, parts_used: used });
  };

  const save = async (extraStatus = null) => {
    const payload = {
      fault_description: job.fault_description,
      assigned_technician_id: job.assigned_technician_id,
      priority: job.priority,
      status: extraStatus || job.status,
      work_done: job.work_done,
      pdf_note: job.pdf_note,
      parts_used: job.parts_used,
      labor_fee: Number(job.labor_fee) || 0,
    };
    try {
      const { data } = await api.put(`/jobs/${id}`, payload);
      setJob(data);
      toast.success("Kayıt güncellendi");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const downloadPDF = async () => {
    try {
      await generateServicePDF(job);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    }
  };

  const deleteJob = async () => {
    if (!(await ask("Servis kaydı silinsin mi?", "Bağlı kasa hareketleri geri alınır ve kullanılan parçalar stoğa iade edilir."))) return;
    const { data } = await api.delete(`/jobs/${id}`);
    toast.success(`Silindi. ${data.reverted_cash_transactions || 0} kasa kaydı geri alındı.`);
    window.location.href = "/admin/jobs";
  };

  const doReassign = async () => {
    if (!reassignForm.technician_id) { toast.error("Teknisyen seçin"); return; }
    try {
      const { data } = await api.post(`/jobs/${id}/reassign`, reassignForm);
      toast.success(reassignForm.keep_history ? `Yeni iş emri oluşturuldu: ${data.job_number}` : "İş yeni teknisyene atandı");
      setReassignOpen(false);
      setReassignForm({ technician_id: "", note: "", keep_history: true });
      if (reassignForm.keep_history) {
        window.location.href = `/admin/jobs/${data.id}`;
      } else {
        load();
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  const partsTotal = (job.parts_used || []).reduce((s, p) => s + (Number(p.total) || 0), 0);
  const discount = Number(job.discount_amount) || 0;
  const total = Math.max(0, partsTotal + (Number(job.labor_fee) || 0) - discount);

  return (
    <div className="space-y-6" data-testid="job-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/admin/jobs" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Servis Listesi
        </Link>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setMsgOpen(true)} data-testid="admin-msg-button">Müşteriye Mesaj</Button>
          <Button variant="outline" onClick={async () => { try { const { data } = await api.post(`/jobs/${job.id}/invoice`, {}); toast.success(`Fatura kaydı: ${data.number} (Muhasebe → KDV & Fatura)`); } catch (e) { toast.error(e.response?.data?.detail || "Fatura oluşturulamadı"); } }} data-testid="create-invoice">Fatura oluştur</Button>
          <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="edit-job-meta"><Pencil className="h-4 w-4 mr-2" /> Kaydı Düzenle</Button>
          <Button variant="outline" onClick={() => setReassignOpen(true)} data-testid="reassign-button"><RefreshCw className="h-4 w-4 mr-2" /> Tekrar Ata</Button>
          <Button variant="outline" onClick={downloadPDF} data-testid="download-pdf-button"><FileDown className="h-4 w-4 mr-2" /> PDF İndir</Button>
          <Button variant="outline" className="text-rose-600" onClick={deleteJob}><Trash2 className="h-4 w-4 mr-2" /> Sil</Button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-mono text-blue-700 text-sm font-bold">{job.job_number}</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">{job.customer_name}</h1>
          <div className="text-sm text-slate-500 mt-1">{job.customer_phone} • {job.customer_address}</div>
          {job.original_job_number && (
            <div className="text-xs text-amber-700 mt-1 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded">
              <RefreshCw className="h-3 w-3" /> Tekrar arıza • Önceki: {job.original_job_number}
            </div>
          )}
        </div>
        <span className={`status-pill border ${STATUS_LABELS[job.status]?.color}`}>{STATUS_LABELS[job.status]?.label}</span>
      </div>

      {job.status === "iptal" && job.cancel_reason && (
        <Card className="p-4 border border-rose-200 bg-rose-50/40 shadow-none">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-rose-600 mt-0.5" />
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-rose-800">Müşteri Vazgeçti</div>
              <div className="text-sm text-rose-900 mt-1">{job.cancel_reason}</div>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-slate-200 shadow-none lg:col-span-2 space-y-5">
          <div>
            <Label>Arıza Açıklaması</Label>
            <Textarea rows={3} value={job.fault_description || ""} onChange={(e) => updateField("fault_description", e.target.value)} />
          </div>
          <div>
            <Label>Yapılan İş / Notlar</Label>
            <Textarea rows={3} value={job.work_done || ""} onChange={(e) => updateField("work_done", e.target.value)} placeholder="Teknisyen tarafından yapılan işlemler..." />
            <NoteAssist value={job.work_done || ""} onChange={(v) => updateField("work_done", v)} />
          </div>
          <div>
            <Label>📝 PDF Notu (servis fişinde görünür)</Label>
            <Textarea rows={2} data-testid="pdf-note-input" value={job.pdf_note || ""} onChange={(e) => updateField("pdf_note", e.target.value)} placeholder="Müşteriye iletilecek özel notlar, garanti uyarıları, vb." />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <Label>Kullanılan Yedek Parçalar</Label>
              <Button size="sm" variant="outline" onClick={addPart}><Plus className="h-3.5 w-3.5 mr-1" /> Ekle</Button>
            </div>
            <div className="space-y-2">
              {(job.parts_used || []).map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Select value={p.part_id || "manual"} onValueChange={(v) => updatePart(i, "part_id", v === "manual" ? "" : v)}>
                    <SelectTrigger className="col-span-4"><SelectValue placeholder="Parça" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manuel giriş</SelectItem>
                      {parts.map((pp) => <SelectItem key={pp.id} value={pp.id}>{pp.name} ({pp.stock_qty} {pp.unit})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input className="col-span-3" placeholder="Adı" value={p.name} onChange={(e) => updatePart(i, "name", e.target.value)} />
                  <Input className="col-span-1" type="number" value={p.qty} onChange={(e) => updatePart(i, "qty", e.target.value)} />
                  <Input className="col-span-2" type="number" value={p.unit_price} onChange={(e) => updatePart(i, "unit_price", e.target.value)} />
                  <div className="col-span-1 text-right font-mono text-sm">{formatTL(p.total)}</div>
                  <Button size="sm" variant="ghost" className="col-span-1 text-rose-600" onClick={() => removePart(i)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              {(!job.parts_used || job.parts_used.length === 0) && (
                <div className="text-center text-sm text-slate-400 py-4">Henüz parça eklenmemiş</div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-200">
            <div>
              <Label>İşçilik Ücreti (₺)</Label>
              <Input type="number" value={job.labor_fee || 0} onChange={(e) => updateField("labor_fee", e.target.value)} data-testid="labor-fee-input" />
            </div>
            <div className="flex flex-col justify-end">
              <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Parça Toplam:</span><span className="font-mono">{formatTL(partsTotal)}</span></div>
              {discount > 0 && <div className="flex justify-between text-sm py-1 text-violet-700"><span>İndirim:</span><span className="font-mono">-{formatTL(discount)}</span></div>}
              <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Genel Toplam:</span><span className="font-mono font-bold text-slate-900">{formatTL(total)}</span></div>
              <div className="flex justify-between text-xs py-1 text-amber-700"><span>Şirkete Borç:</span><span className="font-mono">{formatTL(total * (Number(job.technician_commission_percent) || 0) / 100)}</span></div>
            </div>
          </div>

          {/* Foto Galeri */}
          {((job.photos_before && job.photos_before.length > 0) || (job.photos_after && job.photos_after.length > 0)) && (
            <div className="pt-4 border-t border-slate-200">
              <Label className="mb-3 block">📸 Saha Fotoğrafları</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Önce</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(job.photos_before || []).map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer" className="block aspect-square rounded border border-slate-200 overflow-hidden hover:border-blue-400">
                        <img src={u} alt="" className="object-cover w-full h-full" />
                      </a>
                    ))}
                    {(!job.photos_before || job.photos_before.length === 0) && <div className="col-span-3 text-xs text-slate-400 py-3 text-center">Yok</div>}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Sonra</div>
                  <div className="grid grid-cols-3 gap-2">
                    {(job.photos_after || []).map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer" className="block aspect-square rounded border border-slate-200 overflow-hidden hover:border-blue-400">
                        <img src={u} alt="" className="object-cover w-full h-full" />
                      </a>
                    ))}
                    {(!job.photos_after || job.photos_after.length === 0) && <div className="col-span-3 text-xs text-slate-400 py-3 text-center">Yok</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* İmza önizleme */}
          {(job.signature_url || job.tech_signature_url) && (
            <div className="pt-4 border-t border-slate-200">
              <Label className="mb-3 block">✍️ İmzalar</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Müşteri</div>
                  {job.signature_url ? <img src={job.signature_url} alt="müşteri imza" className="border border-slate-200 rounded bg-white p-2 w-full" /> : <div className="text-xs text-slate-400">Yok</div>}
                </div>
                <div>
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Teknisyen</div>
                  {job.tech_signature_url ? <img src={job.tech_signature_url} alt="teknisyen imza" className="border border-slate-200 rounded bg-white p-2 w-full" /> : <div className="text-xs text-slate-400">Yok</div>}
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-6 border border-slate-200 shadow-none space-y-4">
          <div>
            <Label>Durum</Label>
            <Select value={job.status} onValueChange={(v) => updateField("status", v)}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Atanan Teknisyen</Label>
            <Select value={job.assigned_technician_id || "none"} onValueChange={(v) => updateField("assigned_technician_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Atanmamış</SelectItem>
                {techs.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name} (%{t.commission_percent})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <AssignSuggest job={job} onAssign={async (tid) => { try { await api.put(`/jobs/${job.id}`, { assigned_technician_id: tid }); toast.success("Usta atandı"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Atanamadı"); } }} />
          <ConflictHint technicianId={job.assigned_technician_id} start={job.scheduled_at} jobId={job.id} />
          <div>
            <Label>Öncelik</Label>
            <Select value={job.priority} onValueChange={(v) => updateField("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dusuk">Düşük</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="yuksek">Yüksek</SelectItem>
                <SelectItem value="acil">ACİL</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <DiscountBox job={job} onChanged={load} />
          <PaymentBox job={job} onChanged={load} />
          <ChecklistView job={job} />
          <div className="pt-4 border-t border-slate-200 space-y-2 text-sm">
            <div className="font-semibold text-slate-700 mb-2">Cihaz Bilgisi</div>
            <div className="flex justify-between"><span className="text-slate-500">Marka:</span><span className="font-medium">{job.device_brand_name || "-"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Model:</span><span>{job.device_model || "-"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Seri No:</span><span className="font-mono text-xs">{job.device_serial || "-"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Tip:</span><span>{job.device_type || "-"}</span></div>
          </div>

          <div className="pt-4 border-t border-slate-200 space-y-2 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Oluşturma:</span><span>{formatDateTime(job.created_at)}</span></div>
            {job.started_at && <div className="flex justify-between"><span className="text-slate-500">Başlangıç:</span><span>{formatDateTime(job.started_at)}</span></div>}
            {job.completed_at && <div className="flex justify-between"><span className="text-slate-500">Tamamlanma:</span><span>{formatDateTime(job.completed_at)}</span></div>}
          </div>

          <Button onClick={() => save()} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="save-job-detail-button">
            <Save className="h-4 w-4 mr-2" /> Kaydet
          </Button>
        </Card>
      </div>

      {/* Reassign dialog */}
      <JobMessageDialog open={msgOpen} onOpenChange={setMsgOpen} job={job} />
      <ManualJobDialog open={editOpen} onOpenChange={setEditOpen} job={job} onSaved={() => load()} />
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Arızayı Tekrar Ata</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Müşterinin arızası tekrarladıysa veya başka bir teknisyene devretmek istiyorsanız bu özelliği kullanın.
            </div>
            <div>
              <Label>Yeni Teknisyen *</Label>
              <Select value={reassignForm.technician_id} onValueChange={(v) => setReassignForm({ ...reassignForm, technician_id: v })}>
                <SelectTrigger data-testid="reassign-tech-select"><SelectValue placeholder="Teknisyen seç" /></SelectTrigger>
                <SelectContent>
                  {techs.filter(t => t.is_active).map(t => <SelectItem key={t.id} value={t.id}>{t.name} (%{t.commission_percent})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Not (opsiyonel)</Label>
              <Textarea rows={2} value={reassignForm.note} onChange={(e) => setReassignForm({ ...reassignForm, note: e.target.value })} placeholder="Önceki arızayla ilgili not..." />
            </div>
            <div className="flex items-start gap-2">
              <input type="checkbox" id="kh" checked={reassignForm.keep_history} onChange={(e) => setReassignForm({ ...reassignForm, keep_history: e.target.checked })} className="mt-1" />
              <div>
                <Label htmlFor="kh">Yeni iş emri olarak aç (önerilen)</Label>
                <div className="text-xs text-slate-500">Bu kayıt değişmez; tekrar arıza için yeni numara verilir. Tikli değilse mevcut iş emri yeni teknisyene devredilir.</div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>İptal</Button>
            <Button onClick={doReassign} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-reassign-button">Ata</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmEl}
    </div>
  );
}
